import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClockCounterClockwise, CaretDown, CaretUp, FileText, SpeakerHigh, StopCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";

export default function PolicyDetailPage() {
  const { pid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [policy, setPolicy] = useState(null);
  const [versions, setVersions] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [quizAns, setQuizAns] = useState({});
  const [signedName, setSignedName] = useState("");
  const [sig, setSig] = useState(null);
  const [signBusy, setSignBusy] = useState(false);
  const [listenBusy, setListenBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const readAloud = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    setListenBusy(true);
    try {
      const text = `${policy.title}. ${policy.content || ""}`;
      const chunks = [];
      for (let i = 0; i < text.length; i += 11000) chunks.push(text.slice(i, i + 11000));
      const parts = [];
      for (const c of chunks) {
        const { data } = await api.post("/tts", { text: c });
        parts.push(...(data.parts || []));
      }
      const audio = new Audio();
      audioRef.current = audio;
      let i = 0;
      const playNext = () => {
        if (i >= parts.length) { setPlaying(false); return; }
        audio.src = `${process.env.REACT_APP_BACKEND_URL}${parts[i++]}`;
        audio.play().catch(() => setPlaying(false));
      };
      audio.onended = playNext;
      setPlaying(true);
      playNext();
    } catch (e) { toast.error(formatApiError(e)); }
    setListenBusy(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/policies");
      const list = Array.isArray(data) ? data : data.policies || [];
      const p = list.find((x) => x.id === pid);
      if (!p) {
        toast.error("Policy not found");
        navigate("/policies");
        return;
      }
      setPolicy(p);
      setSignedName((n) => n || user?.name || "");
      try {
        const v = await api.get(`/policies/${pid}/versions`);
        setVersions(v.data.versions || []);
      } catch {
        setVersions([]); // staff can't read version history
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [pid]);

  const signPolicy = async () => {
    if (!signedName.trim()) { toast.error("Type your full name"); return; }
    if (!sig) { toast.error("Please draw your signature"); return; }
    const quiz = policy.quiz || [];
    if (quiz.length && quiz.some((_, i) => quizAns[i] === undefined)) { toast.error("Answer every question before signing"); return; }
    setSignBusy(true);
    try {
      const { data } = await api.post(`/policies/${policy.id}/signoff`, {
        signature_png: sig,
        signed_name: signedName.trim(),
        quiz_answers: quiz.map((_, i) => quizAns[i]),
      });
      toast.success(data.signed?.quiz_total
        ? `Acknowledgment recorded — ${data.signed.quiz_correct}/${data.signed.quiz_total} correct (completion is what counts)`
        : "Acknowledgment recorded — this is your proof");
      setQuizAns({});
      setSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSignBusy(false);
    }
  };

  if (!policy) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const quiz = policy.quiz || [];

  return (
    <div className="max-w-3xl" data-testid="policy-detail-page">
      <button onClick={() => navigate("/policies")} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="policy-detail-back">
        <ArrowLeft size={14} /> All policies
      </button>

      <div className="rounded-lg border border-stone-200 bg-white p-8 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="policy-detail-title">{policy.title}</h1>
          <div className="flex shrink-0 items-center gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/policies/${policy.id}/pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white"
              data-testid="policy-print">
              Print / PDF
            </a>
            <button onClick={readAloud} disabled={listenBusy} data-testid="policy-read-aloud"
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${playing ? "border-primary bg-primary text-white" : "border-stone-300 text-stone-600 hover:bg-stone-900 hover:text-white"}`}>
              {playing ? <><StopCircle size={16} /> Stop</> : <><SpeakerHigh size={16} /> {listenBusy ? "Preparing…" : "Read aloud"}</>}
            </button>
          </div>
        </div>
        <p className="mt-1 text-xs text-stone-400" data-testid="policy-detail-meta">
          {policy.category ? `${policy.category} · ` : ""}Version {policy.version}
          {policy.published_at ? ` · published ${String(policy.published_at).slice(0, 10)}` : ""}
          {policy.updated_at ? ` · updated ${String(policy.updated_at).slice(0, 10)}` : ""}
        </p>
        {(policy.aligned_sources || []).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="policy-source-badges">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Aligns with:</span>
            {policy.aligned_sources.map((s, i) => (
              <span key={i} className="rounded-full border border-primary/30 bg-accent px-2.5 py-0.5 text-[10px] font-semibold text-accent-foreground" title={s.title} data-testid={`policy-source-badge-${i}`}>
                {s.authority ? `${s.authority} — ` : ""}{s.title}
              </span>
            ))}
          </div>
        )}
        {policy.has_source && (
          <a href={`${api.defaults.baseURL}/policies/${policy.id}/source`} target="_blank" rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="policy-detail-source">
            <FileText size={13} /> View original source document
          </a>
        )}
        <div className="mt-6 border-t border-stone-100 pt-6" data-testid="policy-detail-content">
          <p className="whitespace-pre-wrap text-[15px] leading-8 text-stone-800">{policy.content}</p>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="policy-changelog">
        <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900">
          <ClockCounterClockwise size={16} className="text-stone-400" /> Change log
        </p>
        {versions === null ? (
          <div className="flex justify-center py-6"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
        ) : versions.length === 0 ? (
          <p className="mt-2 text-xs text-stone-400">No previous versions — this is the original text.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            <li className="rounded-md border border-[#27AE60]/30 bg-[#27AE60]/5 px-3 py-2" data-testid="changelog-current">
              <p className="text-xs font-semibold text-[#1e8449]">Current — v{policy.version}</p>
            </li>
            {versions.map((v) => (
              <li key={v.id} className="rounded-md border border-stone-200" data-testid={`changelog-v${v.version}`}>
                <button onClick={() => setExpanded((e) => (e === v.id ? null : v.id))}
                  className="flex w-full items-center justify-between px-3 py-2 text-left">
                  <span className="text-xs font-medium text-stone-700">
                    v{v.version} — {v.note || "saved version"} <span className="text-stone-400">· {String(v.saved_at || "").slice(0, 10)} · {v.saved_by}</span>
                  </span>
                  {expanded === v.id ? <CaretUp size={14} className="text-stone-400" /> : <CaretDown size={14} className="text-stone-400" />}
                </button>
                {expanded === v.id && (
                  <div className="border-t border-stone-100 bg-stone-50 p-3">
                    <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-xs leading-6 text-stone-600">{v.content}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {policy.require_signoff && policy.my_signoff_status === "current" ? (
        <div className="mt-6 rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/10 p-4 text-sm text-[#1e8449]" data-testid="policy-signed-banner">
          You have acknowledged version {policy.signoff_version_required} of this policy. Your signed submission is on file.
        </div>
      ) : policy.require_signoff ? (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="policy-signoff-form">
          <p className="font-heading text-sm font-semibold text-stone-900">Acknowledgment{quiz.length ? " & comprehension check" : ""}</p>
          {policy.my_signoff_status === "stale" && (
            <p className="mt-1 text-xs font-medium text-[#8a5d14]" data-testid="policy-resign-note">This policy changed since you last signed — please review and re-acknowledge.</p>
          )}
          {quiz.length > 0 && (
            <div className="mt-3 space-y-4" data-testid="policy-quiz">
              <p className="text-xs text-stone-500">Answer every question — completion counts, not a passing score.</p>
              {quiz.map((q, i) => (
                <div key={`${i}-${(q.q || "").slice(0, 24)}`} className="rounded-md border border-stone-100 bg-stone-50 p-3" data-testid={`policy-quiz-q-${i}`}>
                  <p className="text-xs font-semibold text-stone-700">{i + 1}. {q.q}</p>
                  <div className="mt-2 space-y-1.5">
                    {(q.choices || []).map((c, j) => (
                      <label key={`${i}-${j}-${(c || "").slice(0, 16)}`} className="flex items-center gap-2 text-xs text-stone-600">
                        <input type="radio" name={`pq-${policy.id}-${i}`} checked={quizAns[i] === j}
                          onChange={() => setQuizAns((a) => ({ ...a, [i]: j }))} data-testid={`policy-quiz-q${i}-c${j}`} />
                        {c}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-3">
            <p className="text-xs italic text-stone-500">I acknowledge that I have read and understand this policy and agree to follow it.</p>
            <div className="space-y-1.5">
              <Label>Your full name *</Label>
              <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} className="border-stone-300 bg-white" data-testid="policy-sign-name" />
            </div>
            <SignaturePad onChange={setSig} />
            <Button onClick={signPolicy} disabled={signBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="policy-sign-submit">
              {signBusy ? "Recording…" : "Sign acknowledgment"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
